// src/pages/search/admin/AdminSearchPage.tsx
// Admin index management — replaces the generated CRUD scaffold at
// /searchs (SearchListPage/DetailPage/CreatePage/EditPage/SettingsPage),
// which modeled "a search" (a SearchIndex config record) with a table whose
// columns (Urn/Title/Subtitle/Icon/Color) don't exist on SearchIndex at all,
// and whose delete button called the wrong RPC (search.DeleteDocument
// instead of DeleteSearchIndex). See docs/ux/search-ia.md.

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Button, Badge } from '@react/components';
import { searchStore } from '@core/stores/search/SearchStore';
import { errMessage } from '@/features/search/format';
import { IndexDetailPanel } from '@/features/search/IndexDetailPanel';
import type { SearchIndex, PeerPolicyValidation } from '@core/types/search';

type Tab = 'indexes' | 'federation';

export const AdminSearchPage = () => {
  const { t } = useI18n();
  const [tab, setTab] = useState<Tab>('indexes');

  return (
    <div className="flex h-full">
      <div className="flex-1 min-w-0 space-y-4 p-6">
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('search.admin.title')}</h1>

        <div className="flex border-b border-border">
          {(['indexes', 'federation'] as Tab[]).map((tb) => (
            <button
              key={tb}
              onClick={() => setTab(tb)}
              className={`border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                tab === tb ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t(`search.admin.tab2.${tb}`)}
            </button>
          ))}
        </div>

        {tab === 'indexes' ? <IndexesTab /> : <FederationTab />}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Indexes tab
// ---------------------------------------------------------------------------

function IndexesTab() {
  const { t } = useI18n();
  const [items, setItems] = useState<SearchIndex[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [productFilter, setProductFilter] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newProductSlug, setNewProductSlug] = useState('');
  const [newEntityTypes, setNewEntityTypes] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const load = () => {
    setIsLoading(true);
    setError(null);
    const params: any = { pagination: { cursor: '', limit: 50, direction: 'forward' }, sort: [], view: 0 };
    if (productFilter) params.product_slug = productFilter;
    searchStore.ListSearchIndexes(params)
      .then(() => setItems(searchStore.items))
      .catch((e) => setError(errMessage(e, t('search.admin.loadError'))))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => { load(); }, [productFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim() || !newProductSlug.trim()) return;
    setIsCreating(true);
    try {
      const created = await searchStore.CreateSearchIndex({
        product_slug: newProductSlug.trim(),
        name: newName.trim(),
        entity_types: newEntityTypes.split(',').map((s) => s.trim()).filter(Boolean),
        index_fields: [],
      });
      setItems((prev) => [created, ...prev]);
      setCreateOpen(false);
      setNewName('');
      setNewProductSlug('');
      setNewEntityTypes('');
    } catch (e) {
      setError(errMessage(e, t('search.admin.saveError')));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="flex gap-4">
      <div className="min-w-0 flex-1 space-y-3">
        <div className="flex items-center gap-2">
          <input
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
            placeholder={t('search.admin.productFilterPlaceholder')}
            className="w-56 rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <Button size="sm" onClick={() => setCreateOpen((o) => !o)}>{t('search.admin.createIndex')}</Button>
        </div>

        {createOpen && (
          <form onSubmit={handleCreate} className="space-y-2 rounded-md border border-border p-3">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={t('search.admin.field.name')}
              className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <input
              value={newProductSlug}
              onChange={(e) => setNewProductSlug(e.target.value)}
              placeholder={t('search.admin.field.productSlug')}
              className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <input
              value={newEntityTypes}
              onChange={(e) => setNewEntityTypes(e.target.value)}
              placeholder={t('search.admin.field.entityTypesPlaceholder')}
              className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setCreateOpen(false)} className="rounded-md px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted">
                {t('common.cancel')}
              </button>
              <Button type="submit" size="sm" isLoading={isCreating} disabled={!newName.trim() || !newProductSlug.trim()}>
                {t('common.create')}
              </Button>
            </div>
          </form>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}
        {isLoading ? (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('search.admin.empty')}</p>
        ) : (
          <ul className="space-y-1">
            {items.map((idx) => (
              <li key={idx.entity?.id}>
                <button
                  onClick={() => setOpenId(idx.entity?.id ?? null)}
                  className="flex w-full items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-left text-sm hover:bg-accent"
                >
                  <div className="min-w-0">
                    <p className="truncate text-foreground">{idx.name}</p>
                    <p className="truncate text-xs text-muted-foreground">{idx.product_slug} · {idx.document_count} {t('search.admin.documentsSuffix')}</p>
                  </div>
                  <Badge variant={idx.is_active ? 'default' : 'secondary'} size="sm">
                    {idx.is_active ? t('search.admin.active') : t('search.admin.inactive')}
                  </Badge>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {openId && (
        <IndexDetailPanel
          indexId={openId}
          onClose={() => setOpenId(null)}
          onDeleted={(id) => setItems((prev) => prev.filter((i) => i.entity?.id !== id))}
          onUpdated={(updated) => setItems((prev) => prev.map((i) => (i.entity?.id === updated.entity?.id ? updated : i)))}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Federation tab — ValidateSearchPolicy
// ---------------------------------------------------------------------------

function FederationTab() {
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  const [entityTypes, setEntityTypes] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [result, setResult] = useState<{ is_valid: boolean; peer_validations: PeerPolicyValidation[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleValidate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsValidating(true);
    setError(null);
    try {
      const res = await searchStore.ValidateSearchPolicy({
        query: {
          query,
          filters: [],
          facet_requests: [],
          product_slugs: [],
          entity_types: entityTypes.split(',').map((s) => s.trim()).filter(Boolean),
          highlight: false,
          snippet_length: 0,
          sort_by: '',
          include_federated: true,
          federated_peer_ids: [],
        } as any,
      });
      setResult(res);
    } catch (e) {
      setError(errMessage(e, t('search.admin.federation.error')));
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <div className="max-w-xl space-y-4">
      <p className="text-sm text-muted-foreground">{t('search.admin.federation.description')}</p>
      <form onSubmit={handleValidate} className="space-y-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('search.admin.federation.queryPlaceholder')}
          className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <input
          value={entityTypes}
          onChange={(e) => setEntityTypes(e.target.value)}
          placeholder={t('search.admin.field.entityTypesPlaceholder')}
          className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <Button type="submit" size="sm" isLoading={isValidating}>{t('search.admin.federation.validate')}</Button>
      </form>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {result && (
        <div className="space-y-2">
          <Badge variant={result.is_valid ? 'default' : 'error'}>
            {result.is_valid ? t('search.admin.federation.valid') : t('search.admin.federation.invalid')}
          </Badge>
          <ul className="space-y-1.5">
            {result.peer_validations.map((p) => (
              <li key={p.peer_platform_id} className="rounded-md border border-border px-3 py-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-foreground">{p.peer_name || p.peer_platform_id}</span>
                  <Badge variant={p.is_allowed ? 'default' : 'error'} size="sm">
                    {p.is_allowed ? t('search.admin.federation.allowed') : t('search.admin.federation.blocked')}
                  </Badge>
                </div>
                {!p.is_allowed && p.denial_reason && <p className="mt-1 text-xs text-muted-foreground">{p.denial_reason}</p>}
                {p.blocked_entity_types.length > 0 && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('search.admin.federation.blockedTypes')}: {p.blocked_entity_types.join(', ')}
                  </p>
                )}
              </li>
            ))}
            {result.peer_validations.length === 0 && (
              <li className="text-sm text-muted-foreground">{t('search.admin.federation.noPeers')}</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default AdminSearchPage;
